/**
 * Polaris Compatibility Layer
 * 
 * Provides React components that mimic Shopify Polaris API
 * while using custom styling that matches Polaris design.
 * 
 * This allows gradual migration to full Polaris when ready.
 */

import React, { useState, useEffect } from 'react';

// Card Component
export const Card = ({ title, sectioned, subdued, children, actions }) => (
    <div className={`card ${subdued ? 'card-subdued' : ''}`}>
        {title && (
            <div className="card-header">
                <span>{title}</span>
                {actions && (
                    <div className="card-actions">
                        {actions.map((action, idx) => (
                            <button key={idx} className="btn btn-slim btn-secondary" onClick={action.onAction}>
                                {action.content}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        )}
        <div className={`card-body ${sectioned ? 'sectioned' : ''}`}>
            {children}
        </div>
    </div>
);

// FormLayout Component
export const FormLayout = ({ children }) => (
    <div className="form-layout">{children}</div>
);

FormLayout.Group = ({ children }) => (
    <div className="form-layout-group">{children}</div>
);

// TextField Component
export const TextField = ({
    label,
    value,
    onChange,
    type = 'text',
    placeholder,
    helpText,
    prefix,
    suffix,
    disabled,
    multiline,
    step,
    error
}) => (
    <div className={`form-group ${error ? 'has-error' : ''}`}>
        {label && <label>{label}</label>}
        <div className="input-wrapper">
            {prefix && <span className="input-prefix">{prefix}</span>}
            {multiline ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    rows={multiline}
                />
            ) : (
                <input
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    step={step}
                />
            )}
            {suffix && <span className="input-suffix">{suffix}</span>}
        </div>
        {helpText && <span className="help-text">{helpText}</span>}
        {error && <span className="error-text">{error}</span>}
    </div>
);

// Select Component
export const Select = ({ label, options, value, onChange, disabled, helpText }) => (
    <div className="form-group">
        {label && <label>{label}</label>}
        <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
            {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                    {opt.label}
                </option>
            ))}
        </select>
        {helpText && <span className="help-text">{helpText}</span>}
    </div>
);

// Button Component
export const Button = ({
    children,
    onClick,
    primary,
    destructive,
    disabled,
    loading,
    size,
    url
}) => {
    const classNames = [
        'btn',
        primary && 'btn-primary',
        destructive && 'btn-destructive',
        !primary && !destructive && 'btn-secondary',
        size === 'slim' && 'btn-slim'
    ].filter(Boolean).join(' ');

    if (url) {
        return (
            <a href={url} className={classNames}>
                {loading ? 'Loading...' : children}
            </a>
        );
    }

    return (
        <button className={classNames} onClick={onClick} disabled={disabled || loading}>
            {loading ? 'Loading...' : children}
        </button>
    );
};

// Stack Component
export const Stack = ({ children, vertical, spacing, distribution, alignment }) => {
    const style = {
        display: 'flex',
        flexDirection: vertical ? 'column' : 'row',
        gap: spacing === 'tight' ? '8px' : spacing === 'extraTight' ? '4px' : '12px',
        justifyContent: distribution === 'equalSpacing' ? 'space-between' :
            distribution === 'trailing' ? 'flex-end' : 'flex-start',
        alignItems: alignment === 'center' ? 'center' : 'flex-start'
    };

    return <div style={style}>{children}</div>;
};

// Badge Component
export const Badge = ({ children, status }) => {
    const statusClass = status ? `badge-${status}` : '';
    return <span className={`badge ${statusClass}`}>{children}</span>;
};

// Banner Component
export const Banner = ({ title, status = 'info', children, onDismiss }) => (
    <div className={`banner banner-${status}`}>
        <div className="banner-content">
            {title && <div className="banner-title">{title}</div>}
            {children}
        </div>
        {onDismiss && (
            <button className="banner-dismiss" onClick={onDismiss}>×</button>
        )}
    </div>
);

// Modal Component
export const Modal = ({
    open,
    onClose,
    title,
    children,
    primaryAction,
    secondaryActions
}) => {
    if (!open) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{title}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">{children}</div>
                <div className="modal-footer">
                    {secondaryActions?.map((action, idx) => (
                        <Button key={idx} onClick={action.onAction}>
                            {action.content}
                        </Button>
                    ))}
                    {primaryAction && (
                        <Button primary onClick={primaryAction.onAction}>
                            {primaryAction.content}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

Modal.Section = ({ children }) => (
    <div className="modal-section">{children}</div>
);

// DataTable Component
export const DataTable = ({ headings, rows, columnContentTypes }) => (
    <table className="data-table">
        <thead>
            <tr>
                {headings.map((heading, idx) => (
                    <th key={idx} style={{ textAlign: columnContentTypes?.[idx] === 'numeric' ? 'right' : 'left' }}>
                        {heading}
                    </th>
                ))}
            </tr>
        </thead>
        <tbody>
            {rows.map((row, rowIdx) => (
                <tr key={rowIdx}>
                    {row.map((cell, cellIdx) => (
                        <td key={cellIdx} style={{ textAlign: columnContentTypes?.[cellIdx] === 'numeric' ? 'right' : 'left' }}>
                            {cell}
                        </td>
                    ))}
                </tr>
            ))}
        </tbody>
    </table>
);

// Tabs Component
export const Tabs = ({ tabs, selected, onSelect, children }) => (
    <div className="tabs-container">
        <div className="tabs">
            {tabs.map((tab, idx) => (
                <button
                    key={tab.id}
                    className={`tab ${selected === idx ? 'active' : ''}`}
                    onClick={() => onSelect(idx)}
                >
                    {tab.content}
                </button>
            ))}
        </div>
        <div className="tab-panel">
            {children}
        </div>
    </div>
);

// Spinner Component
export const Spinner = ({ size = 'medium' }) => (
    <div className={`spinner spinner-${size}`}>
        <svg viewBox="0 0 44 44">
            <circle cx="22" cy="22" r="20" fill="none" strokeWidth="4" />
        </svg>
    </div>
);

// Toast Component
export const Toast = ({ content, error, onDismiss, duration = 3000 }) => {
    useEffect(() => {
        const timer = setTimeout(onDismiss, duration);
        return () => clearTimeout(timer);
    }, [duration, onDismiss]);

    return (
        <div className={`toast ${error ? 'toast-error' : ''}`}>
            {content}
        </div>
    );
};

// TextContainer Component
export const TextContainer = ({ children }) => (
    <div className="text-container">{children}</div>
);

// TextStyle Component
export const TextStyle = ({ children, variation }) => {
    const style = {};

    if (variation === 'strong') {
        style.fontWeight = 600;
    } else if (variation === 'subdued') {
        style.color = '#6D7175';
    }

    return <span style={style}>{children}</span>;
};

// Form Component
export const Form = ({ children, onSubmit }) => (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit?.(); }}>
        {children}
    </form>
);

export default {
    Card,
    FormLayout,
    TextField,
    Select,
    Button,
    Stack,
    Badge,
    Banner,
    Modal,
    DataTable,
    Tabs,
    Spinner,
    Toast,
    TextContainer,
    TextStyle,
    Form
};
